<template>
  <header v-if="headerSettings" class="header-area-sticky no-print">
    <oie-my-machines-selection-banner></oie-my-machines-selection-banner>
    <oie-the-superuser-header v-if="clientModel.impersonating"></oie-the-superuser-header>

    <oie-the-top-message-collection :messages="headerSettings.messages"></oie-the-top-message-collection>

    <div
      v-if="mq('small')"
      class="header-top">
      <div class="header-top__container body-smaller-semi-bold">
        <oie-list-price-switch v-if="clientModel.isAuthenticated && clientModel.hasCustomerPrices" is-for="list-price"></oie-list-price-switch>

        <oie-vat-switch is-for="vat-switch" :force-off="clientModel.hideVatSwitch"></oie-vat-switch>

        <!-- Mobile component -->
        <oie-navigation-market-selector
          v-if="mq('to-small')"
          :referrer-url="headerSettings.referrerUrl"
          :available-markets="headerSettings.availableMarkets">
        </oie-navigation-market-selector>

        <!-- Desktop component -->
        <oie-the-header-market-selector
          v-show="mq('small')"
          :referrer-url="headerSettings.referrerUrl"
          :available-markets="headerSettings.availableMarkets">
        </oie-the-header-market-selector>

        <oie-base-action
          v-if="mq('medium')"
          v-tooltip="tStatic('customerServiceInCurrentLanguage') ? {content: tStatic('customerServiceInCurrentLanguage'), placement: 'bottom', classes: 'inverted'} : undefined"
          theme="link"
          :aria-label="`${tStatic('phone')} ${headerSettings.startPageReference.companyPhoneTitle}`"
          :url="`tel:${headerSettings.startPageReference.companyPhone}`">
          <template #iconPrepend>
            <oie-base-icon icon="phone"></oie-base-icon>
          </template>
          {{ headerSettings.startPageReference.companyPhoneTitle }}
          <template v-if="tStatic('customerServiceInCurrentLanguage')" #iconAppend>
            <oie-base-image
              :images="`/globalassets/languages/country_${clientModel.currentLanguage}.webp`"
              alt-text=""
              image-width="15px"
              image-height="15px"></oie-base-image>
          </template>
        </oie-base-action>

        <oie-base-action v-if="mq('medium')" theme="link" :url="headerSettings.startPageReference.contactMenuUrl">
          <template #iconPrepend>
            <oie-base-icon icon="chat"></oie-base-icon>
          </template>
          {{ headerSettings.startPageReference.contactMenuUrlTitle }}
        </oie-base-action>

        <oie-base-action v-if="!clientModel.isAuthenticated" theme="link" :url="headerSettings.loginUrl">
          <template #iconPrepend>
            <oie-base-icon icon="user"></oie-base-icon>
          </template>
          {{ headerSettings.startPageReference.loginMenuTitle }}
        </oie-base-action>

        <oie-the-header-account-action v-if="clientModel.isAuthenticated"></oie-the-header-account-action>
      </div>
    </div>

    <div class="header-bottom">
      <div class="header-bottom__container">
        <a :href="headerSettings.logoUrl" class="header-bottom__logo-link">
          <oie-use-svg svg="olsson-logo" class="header-bottom__logo-link-svg"></oie-use-svg>
        </a>

        <oie-instant-search class="header-bottom__search"></oie-instant-search>

        <div v-if="mq('medium')" class="header-bottom__actions">
          <a
            v-if="clientModel.isAuthenticated && clientModel.enableShoppingListExtended"
            :href="headerSettings.startPageReference.fastOrderMenuUrl"
            class="button button--ghost button--icon-left header-bottom__fast-order-button"
            active-class="button--ghost-is-active"
            @click.stop="handleExternalMyPagesNavigation(get(headerSettings, 'startPageReference.fastOrderMenuUrl'))">
            <oie-use-svg svg="fastlist"></oie-use-svg>
            <div class="header-bottom__fast-order-text">
              {{ headerSettings.startPageReference.fastOrderMenuTitle }}
            </div>
          </a>
          <oie-the-header-cart></oie-the-header-cart>
        </div>

        <oie-the-header-actions></oie-the-header-actions>

        <oie-instant-result></oie-instant-result>
      </div>
    </div>

    <oie-the-header-navigation :items="headerSettings.headerNavigationItems">
    </oie-the-header-navigation>
    <transition name="slide-in">
      <oie-slide-in-navigation v-if="mq('to-small') && isNavigationOpen" :header-settings="headerSettings"></oie-slide-in-navigation>
    </transition>
  </header>
</template>

<script>
import { mapGetters } from 'vuex'
import { mixinExternal } from '@/mixins/my-pages'

export default {
  mixins: [mixinExternal],
  props: {
    headerSettings: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters('ui', ['isNavigationOpen'])
  }
}

</script>

<style lang="scss">
/* header area */
.header-area-sticky {
  position: sticky;
  top: 0;
  z-index: 101;
}

/* header top bar */

.header-top {
  background-color: color(bar-background);
}

.header-top--lower-z-index {
  z-index: 0;
}

.header-top__container {
  @include container;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 2.1875rem;
  color: color(bar-text);
}

.header-top__container > *:not(:last-child) {
  margin-right: measure(full);

  @include media(large) {
    margin-right: measure(double);
  }
}

/* header */

.header-bottom {
  background-color: color(header-background);
  color: color(text-inverted);
  z-index: 11;
  position: sticky;

  @include media(small) {
    top: 2.1875rem;
  }
}

.header-bottom__container {
  @include container;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  height: auto;
  min-height: 4rem;
  padding-top: calc(#{measure(quarter)} + #{measure(eighth)});
  padding-bottom: calc(#{measure(quarter)} + #{measure(eighth)});

  @include media(medium) {
    position: relative;
    height: 5.25rem;
    flex-wrap: nowrap;
  }
}

.header-bottom__logo-link {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 8.375rem;
  color: color(logo-white);
  margin-right: measure(half);
  @include media(small) {
    max-width: 9.8rem;
    margin-right: measure(full);
  }

  @include media(medium) {
    max-width: 13.5rem;
  }

  @include media(target-ie) {
    flex-basis: 13.5rem;
  }
}

.header-bottom__logo-link-svg {
  width: 100%;
  height: auto;
}

.header-bottom__search {
  flex-basis: 100%;
  order: 2;
  transition: max-height 200ms ease-out;

  @include media(to-medium) {
    display: none;
  }

  @include media(medium) {
    order: initial;
  }

  @include media(target-ie) {
    flex-grow: 1;
    flex-basis: auto;
  }
}

.header-bottom__search--is-expanded {
  display: block;
}

.header-bottom__search--remove-overflow {
  @include media(to-medium) {
    overflow: initial;
  }
}

.header-bottom__actions {
  display: flex;
  flex-shrink: 0;
  margin-left: measure(quarter);

  @include media(target-ie) {
    order: 3;
  }
}

.header-bottom__actions > .button:first-child {
  margin-right: measure(quarter);
}

.button--ghost.header-bottom__fast-order-button {
  width: 4.5rem;
  height: 2.125rem;

  @include media(small) {
    height: 2.5rem;
  }

  @include media(large) {
    width: 9.375rem;
  }
}

.header-bottom__fast-order-button svg {
  @include media(to-large) {
    margin-right: 0;
  }
}

.header-bottom__fast-order-text {
  display: none;

  @include media(large) {
    display: block;
  }
}

.navigational-item--fastorder svg {
  margin-right: measure(quarter);
}

// static header navigation

.header-navigation {
  background-color: color(header-background);
}

.header-navigation__container {
  @include container;
  display: flex;
  gap: measure(eighth);
  align-items: center;
  flex-wrap: wrap;
  padding-top: 0;
  padding-top: calc(#{measure(eighth)} + #{measure(eighth)});
  padding-bottom: calc(#{measure(quarter)} + #{measure(eighth)});
}

.header-navigation__list {
  @include reset-list;
  display: grid;
  gap: measure(eighth);
  grid-template-columns: repeat(2, 1fr);
  width: 100%;
  justify-content: center;

  @include media(small) {
    display: flex;
    width: auto;
  }
}

@keyframes slide-in {
    0% {
        transform: translateX(100%);
    }
    100% {
        transform: translateX(0%);
    }
}

.slide-in-enter-active {
  animation: slide-in .3s;
}

.slide-in-leave-active {
  animation: slide-in .3s reverse;
}

</style>
